/**
 * Edge style "A · Transit": every link is a #121212 casing plus an opaque,
 * round-capped coloured line, in screen pixels. Overlays differ by pattern,
 * not colour alone: gold path dashes, Local Optimal route dots, AI a double rail.
 */
import { mixHex } from "../../theme/colorScience";
import type { BoardLink, BoardPalette, LinkType } from "./boardModel";

export const CASING_EXTRA = 3.5;
export const STREAK_STEP = 0.75;
export const STREAK_MAX_LEVEL = 4;
export const AI_RAIL = 1.5;

const BASE: Record<LinkType, number> = {
  player: 2.5,
  gold: 2.25,
  localOptimal: 3,
  ai: 4.5,
};
const DASH: Record<LinkType, string | undefined> = {
  player: undefined,
  gold: "7 6",
  localOptimal: "0.1 6",
  ai: undefined,
};

/** Widths scale with the on-screen node radius: clamp(r / 14, 1, 1.8). */
export function edgeWidthScale(nodeRadiusPx: number): number {
  return Math.max(1, Math.min(1.8, nodeRadiusPx / 14));
}

/** A streak edge warms from the path colour toward gold with its level. */
export function streakColor(level: number, palette: BoardPalette): string {
  if (level <= 0) return palette.path;
  return mixHex(palette.path, palette.gold, Math.min(1, 0.32 + 0.22 * level));
}

export function streakWidthBonus(level: number): number {
  return Math.min(Math.max(level, 0), STREAK_MAX_LEVEL) * STREAK_STEP;
}

export interface EdgeStyle {
  width: number;
  casingWidth: number;
  color: string;
  dash: string | undefined;
  /** Width of the #121212 rail drawn over an AI line, or null. */
  rail: number | null;
}

export function edgeStyle(
  link: BoardLink,
  opts: { palette: BoardPalette; scale: number; won: boolean },
): EdgeStyle {
  const { palette, scale, won } = opts;
  const isPlayer = link.type === "player";
  const width =
    BASE[link.type] * scale + (isPlayer ? streakWidthBonus(link.level) : 0);
  const colorOf: Record<LinkType, string> = {
    player: won ? palette.gold : streakColor(link.level, palette),
    gold: palette.gold,
    localOptimal: palette.purple,
    ai: palette.ai,
  };
  return {
    width,
    casingWidth: width + CASING_EXTRA,
    color: colorOf[link.type],
    dash: DASH[link.type],
    rail: link.type === "ai" ? AI_RAIL * scale : null,
  };
}
